import { ReactNode } from 'react';
import styled from 'styled-components';
import { PublishedPolicy } from './usePublishedPolicy';

const Document = styled.div`
  line-height: 1.8;
  white-space: normal;
  overflow-wrap: anywhere;
  p { margin: 0 0 16px; white-space: pre-line; }
  h2 { font-size: 18px; margin: 32px 0 12px; }
  h3 { font-size: 16px; margin: 28px 0 10px; }
  ul, ol { margin: 0 0 20px; padding-left: 24px; }
  li { margin: 6px 0; }
  a { color: inherit; text-decoration: underline; text-underline-offset: 3px; }
  .policy-table { overflow-x: auto; margin: 16px 0 24px; }
  .policy-table:focus-visible, a:focus-visible { outline: 2px solid currentColor; outline-offset: 3px; }
  table { width: 100%; border-collapse: collapse; font: inherit; text-align: left; }
  table[data-columns="4"] { min-width: 620px; }
  th, td { border: 1px solid var(--user-border); padding: 12px; vertical-align: top; }
  th { background: var(--user-surface-raised); }
`;

// The store's Markdown is rendered as React text, never as executable HTML.
function inline(text: string): ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*|https?:\/\/[^\s<>]+)/g).map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={index}>{part.slice(2, -2)}</strong>;
    if (/^https?:\/\//.test(part)) return <a key={index} href={part}>{part}</a>;
    return part.replace(/\\([\\`*{}[\]()#+.!_>~|-])/g, '$1');
  });
}

function cells(line: string): string[] {
  return line.trim().replace(/^\|/, '').replace(/\|$/, '').split(/(?<!\\)\|/).map(cell => cell.trim());
}

const isTableRow = (line: string) => /^\s*\|.*\|\s*$/.test(line);
const isDivider = (line: string) => isTableRow(line) && cells(line).every(cell => /^:?-{3,}:?$/.test(cell));

const plainTableHeaders = [
  ['처리 목적', '처리 항목', '처리 근거', '보유기간'],
  ['보존 항목', '기간', '근거 법령'],
  ['제공받는 자', '제공 목적', '제공 항목', '보유기간'],
  ['제공받는 자', '목적', '항목', '근거'],
  ['수탁자', '위탁 업무'],
  ['전달받는 자', '목적', '전달 항목', '전달 방법'],
  ['단계', '조치'],
];

function blocks(content: string): ReactNode[] {
  const lines = content.replace(/\r\n?/g, '\n').split('\n');
  // The supplied plain-text documents keep each table cell on its own line.
  // Format them for display without changing the persisted legal text.
  for (let start = 0; start < lines.length; start++) {
    const headers = plainTableHeaders.find(row => row.every((cell, column) => lines[start + column] === cell));
    if (!headers) continue;
    let end = start + headers.length;
    while (end < lines.length && lines[end].trim() && !/^(제\d+조|[①②③④⑤]|위탁 기간)/.test(lines[end])) end++;
    const values = lines.slice(start + headers.length, end);
    if (!values.length || values.length % headers.length !== 0) continue;
    const table = ['| ' + headers.join(' | ') + ' |', '| ' + headers.map(() => '---').join(' | ') + ' |'];
    for (let row = 0; row < values.length; row += headers.length) {
      table.push('| ' + values.slice(row, row + headers.length).map(value => value.replace(/\|/g, '\\|')).join(' | ') + ' |');
    }
    lines.splice(start, end - start, ...table);
    start += table.length - 1;
  }
  const result: ReactNode[] = [];
  let index = 0;
  let heading = '약관';
  while (index < lines.length) {
    const line = lines[index].trim();
    const key = index;
    if (!line) { index++; continue; }
    if (isTableRow(line) && isDivider(lines[index + 1] || '')) {
      const headers = cells(line);
      index += 2;
      const rows: string[][] = [];
      while (index < lines.length && isTableRow(lines[index])) rows.push(cells(lines[index++]));
      result.push(<div className="policy-table" role="region" aria-label={`${heading} 표`} tabIndex={0} key={key}>
        <table data-columns={headers.length}>
          <thead><tr>{headers.map((header, column) => <th scope="col" key={column}>{inline(header)}</th>)}</tr></thead>
          <tbody>{rows.map((row, rowIndex) => <tr key={rowIndex}>{headers.map((_, column) => <td key={column}>{inline(row[column] || '')}</td>)}</tr>)}</tbody>
        </table>
      </div>);
      continue;
    }
    const chapter = line.match(/^#{1,3}\s+(.+)$/) || line.match(/^(제\d+장 .+|부칙|사업자 정보)$/);
    const article = line.match(/^\*\*(.+)\*\*$/) || line.match(/^(제\d+조(?:의\d+)?\s*\(.+\))$/);
    if (chapter || article) {
      heading = (chapter || article)![1];
      result.push(chapter ? <h2 key={key}>{inline(heading)}</h2> : <h3 key={key}>{inline(heading)}</h3>);
      index++;
      continue;
    }
    const list = line.match(/^([-•] |\d+\. )(.+)$/);
    if (list) {
      const ordered = /^\d/.test(list[1]);
      const pattern = ordered ? /^\d+\. (.+)$/ : /^[-•] (.+)$/;
      const items: ReactNode[] = [];
      const start = ordered ? parseInt(list[1], 10) : undefined;
      while (index < lines.length) {
        const item = lines[index].trim().match(pattern);
        if (!item) break;
        items.push(<li key={index++}>{inline(item[1])}</li>);
      }
      result.push(ordered ? <ol key={key} start={start}>{items}</ol> : <ul key={key}>{items}</ul>);
      continue;
    }
    const paragraph = [line];
    index++;
    while (index < lines.length && lines[index].trim()
      && !/^(#{1,3}\s|\*\*|\||[-•] |\d+\. |제\d+장 |제\d+조(?:의\d+)?\s*\(|부칙$|사업자 정보$)/.test(lines[index].trim())) paragraph.push(lines[index++].trim());
    result.push(<p key={key}>{inline(paragraph.join('\n'))}</p>);
  }
  return result;
}

export default function StorePolicyContent({ policy, showMetadata = true }: { policy: PublishedPolicy; showMetadata?: boolean }) {
  return <>
    {showMetadata && <p style={{ color: 'var(--user-text-muted)', fontSize: 12, marginBottom: 24 }}>시행일 {policy.effectiveDate}</p>}
    <Document>{blocks(policy.content)}</Document>
  </>;
}
